import { PageHero } from "@/components/site/PageHero";
import { Section, SectionHeader } from "@/components/site/Section";
import { SymptomGrid } from "@/components/site/SymptomGrid";
import { SymptomRadial } from "@/components/site/SymptomRadial";
import { StepsList } from "@/components/site/StepsList";
import { EvidenceCallout } from "@/components/site/EvidenceCallout";
import { BehaviorList } from "@/components/site/BehaviorList";
import { AdvisorCTA } from "@/components/site/AdvisorCTA";

import { VideoPlaceholder } from "@/components/site/VideoPlaceholder";
import { StatImageCard } from "@/components/site/StatImageCard";

import { LearningPathSection } from "@/components/site/LearningPathSection";
import { LearningPathCard } from "@/components/site/LearningPathCard";
import { pathBySlug } from "@/content/learning-paths";
import type { PageContent } from "@/content/problems";

const SECTION_TITLE_CLASS = "text-3xl font-semibold leading-tight text-ink md:text-4xl";
const SECTION_TITLE_STYLE = { fontFamily: "'Poppins', sans-serif" } as const;

export function PageTemplate({
  page,
  industrySlug,
  heroBackgroundImage,
  heroBackgroundPosition,
  heroSolidPurple,
  symptomsVariant = "cards",
  approachBackgroundImage,
  finalBackgroundImage,
  impactStat,
}: {
  page: PageContent;
  industrySlug?: string;
  heroBackgroundImage?: string;
  heroBackgroundPosition?: string;
  heroSolidPurple?: boolean;
  symptomsVariant?: "cards" | "list" | "radial";
  approachBackgroundImage?: string;
  finalBackgroundImage?: string;
  impactStat?: {
    image?: string;
    alt?: string;
    stat: string;
    body: string;
    source?: string;
    imagePosition?: string;
    placeholder?: boolean;
  };
}) {
  const featuredPath = page.learningPathSlug ? pathBySlug(page.learningPathSlug) : undefined;

  return (
    <>
      <PageHero
        maxWidth="6xl"
        headline={page.headline}
        supporting={page.supporting}
        trustBar={page.trustBar}
        {...(heroBackgroundImage
          ? { backgroundImage: heroBackgroundImage, dark: true }
          : heroSolidPurple
            ? { dark: true }
            : {})}
        {...(heroBackgroundPosition ? { backgroundPosition: heroBackgroundPosition } : {})}
        ctas={[
          { label: page.primaryCTA.label, to: page.primaryCTA.to, variant: "primary" },
          { label: page.secondaryCTA.label, to: page.secondaryCTA.to, variant: "secondary" },
        ]}
      />

      <Section tone="default">
        <SectionHeader
          eyebrow={page.symptomsEyebrow}
          title={page.symptomsTitle}
          intro={page.symptomsIntro}
          titleClassName={SECTION_TITLE_CLASS}
          titleStyle={SECTION_TITLE_STYLE}
        />
        {symptomsVariant === "radial" ? (
          <SymptomRadial items={page.symptoms} />
        ) : (
          <SymptomGrid items={page.symptoms} variant={symptomsVariant} />
        )}
      </Section>

      <Section tone="lavender">
        <div className="grid gap-12 lg:grid-cols-[1.1fr_1fr]">
          <div>
            <SectionHeader
              title={page.impactTitle}
              intro={page.impactIntro}
              titleClassName={SECTION_TITLE_CLASS}
              titleStyle={SECTION_TITLE_STYLE}
            />
            <ul className="space-y-3">
              {page.impactBullets.map((b) => (
                <li
                  key={b}
                  className="flex gap-3 rounded-xl border border-border/60 bg-background/70 p-4 text-sm text-foreground"
                >
                  <span
                    className="mt-1 inline-block h-2 w-2 flex-shrink-0 rounded-full bg-[#693D94]"
                    aria-hidden="true"
                  />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
          {impactStat ? (
            <StatImageCard {...impactStat} />
          ) : (
            page.evidence && (
              <EvidenceCallout
                quote={page.evidence.quote}
                source={page.evidence.source}
                href={page.evidence.href}
              />
            )
          )}
        </div>
      </Section>

      <Section
        {...(approachBackgroundImage
          ? {
              tone: "image" as const,
              backgroundImage: approachBackgroundImage,
              overlayClassName: "bg-[#693D94]/[0.91]",
            }
          : { tone: "default" as const })}
      >
        <SectionHeader
          eyebrow={page.approachEyebrow}
          title={page.approachTitle}
          titleClassName={
            approachBackgroundImage
              ? "text-3xl font-semibold leading-tight text-white md:text-4xl"
              : SECTION_TITLE_CLASS
          }
          titleStyle={SECTION_TITLE_STYLE}
          {...(approachBackgroundImage ? { onDark: true } : {})}
        />
        <StepsList items={page.approachSteps} onDark={Boolean(approachBackgroundImage)} />
      </Section>

      <Section tone="default">
        <SectionHeader
          eyebrow="Watch"
          title={page.videoTitle ?? "See this problem in action"}
          align="center"
          titleClassName={SECTION_TITLE_CLASS}
          titleStyle={SECTION_TITLE_STYLE}
        />

        <div className="mx-auto max-w-4xl">
          <VideoPlaceholder
            {...(page.videoEmbedUrl ? { embedUrl: page.videoEmbedUrl } : {})}
            {...(page.videoEmbedUrlMobile ? { embedUrlMobile: page.videoEmbedUrlMobile } : {})}
            label={page.videoTitle ?? "See this problem in action"}
          />
        </div>
      </Section>

      {industrySlug && <LearningPathSection industrySlug={industrySlug} />}

      {!industrySlug && (
        <Section tone="lavender">
          {featuredPath ? (
            <>
              <SectionHeader
                eyebrow="Learning path"
                title={featuredPath.name}
                titleClassName={SECTION_TITLE_CLASS}
                titleStyle={SECTION_TITLE_STYLE}
              />
              <div className="max-w-xl">
                <LearningPathCard path={featuredPath} />
              </div>
            </>
          ) : (
            <>
              <SectionHeader
                title={page.behaviorsTitle}
                titleClassName={SECTION_TITLE_CLASS}
                titleStyle={SECTION_TITLE_STYLE}
              />

              <BehaviorList items={page.behaviors} />
            </>
          )}
        </Section>
      )}

      <AdvisorCTA
        title={page.finalTitle}
        body={page.finalBody}
        primaryLabel={page.primaryCTA.label}
        primaryTo={page.primaryCTA.to}
        secondaryLabel={page.secondaryCTA.label}
        secondaryTo={page.secondaryCTA.to}
        {...(finalBackgroundImage ? { backgroundImage: finalBackgroundImage } : {})}
      />
    </>
  );
}
